import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { rolesAPI } from '../../api';
import { apiError, parseLimit, togglePermission } from './usersUtils';

const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const inputClass =
  'block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500';

const PermissionGroup = ({ title, items, selected, onToggle, catalogue }) => (
  <fieldset>
    <legend className="text-sm font-semibold text-gray-900 mb-2">{title}</legend>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {items.map((p) => {
        const needs = p.requires && catalogue.find((c) => c.key === p.requires);
        return (
          <label
            key={p.key}
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
              selected.includes(p.key) ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="checkbox"
              checked={selected.includes(p.key)}
              onChange={(e) => onToggle(p.key, e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{p.label}</span>
              <span className="block text-sm text-gray-500">
                {p.description}
                {needs && ` (needs ${needs.label})`}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

/** Make a role (role = null) or change one: name, ticks and discount limit. */
const RoleFormModal = ({ isOpen, onClose, role, catalogue, onSaved }) => {
  const editing = Boolean(role);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [limit, setLimit] = useState('0');
  const [selected, setSelected] = useState([]);
  const [problems, setProblems] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(role?.name || '');
    setDescription(role?.description || '');
    setLimit(String(role?.discount_limit ?? 0));
    setSelected(role?.permissions || []);
    setProblems({});
    setServerError('');
  }, [isOpen, role]);

  const grantable = catalogue.filter((p) => p.grantable);
  const pages = grantable.filter((p) => p.group === 'page');
  const actions = grantable.filter((p) => p.group === 'action');
  const toggle = (key, checked) => setSelected((s) => togglePermission(s, key, checked, catalogue));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = {};
    const cleanName = name.trim().replace(/\s+/g, ' ');
    if (cleanName.length < 2 || cleanName.length > 50) found.name = 'Use 2 to 50 characters.';
    const parsedLimit = parseLimit(limit);
    if (parsedLimit === null) found.limit = 'Type a whole number from 0 to 100.';
    setProblems(found);
    if (Object.keys(found).length) return;

    const payload = {
      description: description.trim(),
      permissions: selected,
      discount_limit: parsedLimit,
    };
    if (!role?.is_system) payload.name = cleanName;
    setSaving(true);
    setServerError('');
    try {
      const response = editing ? await rolesAPI.update(role.id, payload) : await rolesAPI.create(payload);
      onSaved(response.data, !editing);
    } catch (error) {
      setServerError(apiError(error, 'Could not save the role. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editing ? `Edit role: ${role.name}` : 'Add a role'} size="lg">
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {serverError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {serverError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="role-name" className={labelClass}>Role name</label>
            <input
              id="role-name"
              autoFocus={!role?.is_system}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={role?.is_system}
              className={inputClass}
              placeholder="e.g. Stock keeper"
              maxLength={50}
            />
            {problems.name ? (
              <p className="mt-1 text-sm text-red-600">{problems.name}</p>
            ) : (
              role?.is_system && <p className="mt-1 text-sm text-gray-500">Built-in roles keep their names.</p>
            )}
          </div>
          <div>
            <label htmlFor="role-limit" className={labelClass}>Discount limit (%)</label>
            <input
              id="role-limit"
              type="number"
              min="0"
              max="100"
              inputMode="numeric"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className={inputClass}
            />
            {problems.limit && <p className="mt-1 text-sm text-red-600">{problems.limit}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="role-description" className={labelClass}>
            What is this role for? <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input
            id="role-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inputClass}
            maxLength={255}
          />
        </div>

        <PermissionGroup title="Pages they can open" items={pages} selected={selected} onToggle={toggle} catalogue={catalogue} />
        <PermissionGroup title="Extra things they can do" items={actions} selected={selected} onToggle={toggle} catalogue={catalogue} />

        <p className="text-sm text-gray-500">
          Adding people and changing roles is for the Admin role only.
          {selected.length === 0 && ' With no ticks, people with this role can sign in but see no pages.'}
        </p>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save role' : 'Add role'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RoleFormModal;
